import {useFormikContext} from 'formik';
import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import ImageCropModal from '../../../../../components/imageCropModal';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useDeleteDocumentImg from '../../../../../services/hookService/deleteDocumentImg/useDeleteDocumentImg';
import usePostUploadCreditLoanImg from '../../../../../services/hookService/postUploadCreditLoanImg/usePostUploadCreditLoanImg';
import ImageUploadBox from '../../../../shopping-basket/cash-payment/card-to-card-pay/ImageUploadBox';
import {PersonPrefix} from '../credit-loan/Forms/MultiPersonForm/MultiPersonValidation';
import {IBankDocsGeneralForm} from '../credit-loan/LoanDocsPage';

interface DocumentImageUploaderProps {
  title?: string;
  loanRequestGuid: string;
  loanRuleGuid: string;
  loanDocumentGuid: string;
  personPrefix?: PersonPrefix; // Which person this uploader belongs to
  fieldName?: string; // The field name in the person's data (e.g., "check_image", "extra_image_0")
  onImageUploaded?: (file: File | null, uploadedGuid: string | null) => void;
  className?: string;
}

const DocumentImageUploader = ({
  title = 'بارگذاری تصویر',
  loanRequestGuid,
  loanRuleGuid,
  loanDocumentGuid,
  personPrefix,
  fieldName,
  onImageUploaded,
  className = '',
}: DocumentImageUploaderProps) => {
  // Get Formik context (if available)
  const formik =
    personPrefix && fieldName ? useFormikContext<IBankDocsGeneralForm>() : null;

  const {mutate: deleteDocumentImg, isLoading: isDeletingImgLoading} =
    useDeleteDocumentImg();
  // Unique instance ID to prevent state leakage between instances
  const instanceId = useRef(
    `uploader-${loanDocumentGuid}-${Date.now()}-${Math.random()}`,
  ).current;

  // Build the full field path
  const fieldPath =
    personPrefix && fieldName ? `${personPrefix}.${fieldName}` : null;

  // Get Formik field value if available
  const formikFieldValue = fieldPath
    ? formik?.getFieldMeta(fieldPath)?.value
    : undefined;

  // Local state initialized from Formik
  const [uploadedImage, setUploadedImage] = useState<File | null>(() => {
    if (formikFieldValue instanceof File) {
      return formikFieldValue;
    }
    return null;
  });

  const [uploadedImgGuid, setUploadedImgGuid] = useState<string>('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentImage, setCurrentImage] = useState<File | null>(null);

  // Sync local state with Formik when Formik values change
  useEffect(() => {
    if (fieldPath && formik) {
      const currentValue = formik.getFieldMeta(fieldPath).value;

      if (currentValue instanceof File) {
        setUploadedImage(currentValue);
      } else if (currentValue === null || currentValue === undefined) {
        setUploadedImage(null);
        setUploadedImgGuid('');
      }
    }
  }, [formik?.values, fieldPath]);

  // Reset state when document GUID changes (new instance)
  useEffect(() => {
    setUploadedImage(null);
    setUploadedImgGuid('');
    setCurrentImage(null);
    setShowCropModal(false);
  }, [loanDocumentGuid]);

  // Sync with Formik when personPrefix changes
  useEffect(() => {
    if (fieldPath && formik) {
      const currentValue = formik.getFieldMeta(fieldPath).value;

      if (currentValue instanceof File) {
        setUploadedImage(currentValue);
      } else {
        setUploadedImage(null);
        setUploadedImgGuid('');
      }
    }
  }, [personPrefix, fieldName, formik?.values]);

  // In DocumentImageUploader.tsx - Update the onSuccess callback
  const {mutate: uploadFile, isLoading} = usePostUploadCreditLoanImg({
    onSuccess: data => {
      setUploadedImage(currentImage);
      setUploadedImgGuid(data?.guid);
      setShowCropModal(false);

      // Update Formik field if personPrefix and fieldName are provided
      if (formik && personPrefix && fieldName) {
        const fieldPath = `${personPrefix}.${fieldName}`;
        // Store the actual File object in Formik, not just the GUID
        formik.setFieldValue(fieldPath, currentImage);
      }

      // Notify parent if callback provided
      if (onImageUploaded) {
        onImageUploaded(currentImage, data?.guid);
      }
    },
  });

  const handleCropComplete = useCallback(
    (croppedImage: File) => {
      uploadFile({
        loan_request: loanRequestGuid,
        loan_rule: loanRuleGuid,
        loan_document: loanDocumentGuid,
        file: croppedImage,
      });
    },
    [loanRequestGuid, loanRuleGuid, loanDocumentGuid, uploadFile],
  );

  const handleImageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCurrentImage(file);
    setShowCropModal(true);
    e.target.value = ''; // Reset input to allow re-uploading same file
  }, []);

  const clearLocalImage = () => {
    setUploadedImage(null);
    setUploadedImgGuid('');

    if (formik && personPrefix && fieldName) {
      const fieldPath = `${personPrefix}.${fieldName}`;
      formik.setFieldValue(fieldPath, null);
    }

    onImageUploaded?.(null, null);
  };

  const removeCroppedImage = useCallback(async () => {
    if (!uploadedImgGuid) {
      clearLocalImage();
      return;
    }

    deleteDocumentImg(uploadedImgGuid, {
      onSuccess: () => clearLocalImage(),
    });
  }, [
    uploadedImgGuid,
    deleteDocumentImg,
    formik,
    personPrefix,
    fieldName,
    onImageUploaded,
  ]);

  if (isLoading || isDeletingImgLoading) {
    return (
      <div className="flex items-center justify-center w-full py-4">
        <SpinnerLoading />
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="text-sm font-medium text-gray-900">
        {title} <span className="text-red-600 text-md">*</span>
      </p>

      <ImageUploadBox
        variant="sm"
        croppedImageFile={uploadedImage}
        onImageRemove={removeCroppedImage}
        onImageChange={handleImageChange}
        inputId={instanceId}
      />

      {showCropModal &&
        currentImage &&
        createPortal(
          <ImageCropModal
            originalFile={currentImage}
            onComplete={handleCropComplete}
            onClose={() => setShowCropModal(false)}
          />,
          document.body,
        )}
    </div>
  );
};

export default DocumentImageUploader;
